import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  generateBlogCoverImage,
  type BlogCoverImageStyle,
} from "@/services/blogService";

interface GenerateCoverImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPrompt: string;
  onGenerated: (url: string) => void;
}

export function GenerateCoverImageDialog({
  open,
  onOpenChange,
  defaultPrompt,
  onGenerated,
}: GenerateCoverImageDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [style, setStyle] = useState<BlogCoverImageStyle>("photographic");

  useEffect(() => {
    if (open) setPrompt(defaultPrompt);
  }, [open, defaultPrompt]);

  const generateMutation = useMutation({
    mutationFn: () =>
      generateBlogCoverImage({
        prompt: prompt.trim(),
        style,
      }),
    onSuccess: ({ url }) => {
      toast.success(t("blog.editor.ai.toast.coverReady"));
      onGenerated(url);
      onOpenChange(false);
    },
    onError: () => toast.error(t("blog.editor.ai.toast.coverError")),
  });

  const canSubmit = prompt.trim().length >= 3 && !generateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {t("blog.editor.ai.coverTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("blog.editor.ai.coverDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="ai-cover-prompt">
              {t("blog.editor.ai.fields.coverPrompt")}
            </Label>
            <Textarea
              id="ai-cover-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t("blog.editor.ai.fields.coverPromptPlaceholder")}
              rows={3}
              maxLength={500}
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="ai-cover-style">
              {t("blog.editor.ai.fields.coverStyle")}
            </Label>
            <Select
              value={style}
              onValueChange={(v) => setStyle(v as BlogCoverImageStyle)}
            >
              <SelectTrigger id="ai-cover-style">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="photographic">
                  {t("blog.editor.ai.styles.photographic")}
                </SelectItem>
                <SelectItem value="illustration">
                  {t("blog.editor.ai.styles.illustration")}
                </SelectItem>
                <SelectItem value="minimal">
                  {t("blog.editor.ai.styles.minimal")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            {t("blog.editor.ai.coverHint")}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={generateMutation.isPending}
          >
            {t("blog.editor.ai.actions.cancel")}
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            disabled={!canSubmit}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {generateMutation.isPending
              ? t("blog.editor.ai.actions.generatingImage")
              : t("blog.editor.ai.actions.generateImage")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
